import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import LoadingSpinner from "@/components/LoadingSpinner";
import { useSocialCallback, useSsoCallback } from "@/queries/auth";
import { buildSsoLoginRedirectUrl, formatWorkOsOAuthRedirectError } from "@/utils/ssoErrors";

export const AuthenticationPage = () => {
  const [searchParams] = useSearchParams();
  const { mutate: authenticateSocial } = useSocialCallback();
  const { mutate: authenticateSSO } = useSsoCallback();
  const location = useLocation();
  const navigate = useNavigate();
  const hasInitialized = useRef(false);
  const isSSO = location.pathname.includes("sso");

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const oauthError = searchParams.get("error");

  const provider = localStorage.getItem("provider");

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // WorkOS / IdP failures (e.g. attribute mapping) redirect here with
    // `error` + `error_description` instead of an authorization `code`.
    if (oauthError) {
      const message = formatWorkOsOAuthRedirectError(
        oauthError,
        searchParams.get("error_description"),
      );
      navigate(buildSsoLoginRedirectUrl(message), { replace: true });
      return;
    }

    if (code) {
      if (isSSO) {
        authenticateSSO({ code, state: state ?? undefined });
      } else {
        authenticateSocial({ code, state: state ?? "", provider: provider ?? "" });
      }
      return;
    }

    navigate(
      buildSsoLoginRedirectUrl(
        isSSO
          ? "We could not complete sign-in with your identity provider. Please try again."
          : "Invalid authentication response.",
        { sso: isSSO },
      ),
      { replace: true },
    );
  }, [
    code,
    state,
    oauthError,
    authenticateSocial,
    authenticateSSO,
    provider,
    isSSO,
    navigate,
    searchParams,
  ]);

  return (
    <div className="flex min-h-[500px] items-center justify-center">
      <div className="w-full max-w-md rounded-3xl bg-white p-12 text-center">
        <span className="mb-4 text-xl font-bold leading-tight text-gray-900">
          Welcome to PromptLayer!
        </span>
        <p className="mt-2 text-lg text-gray-600">Logging in..</p>
        <div className="mt-4 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    </div>
  );
};
